import validate from "../libs/validate-0.12.0.js";
import {validateVatNumber} from "../utils/jsvat";
import {trans} from "../utils/i18n";

validate.validators.presence.message = trans("frontendValidation.can't be blank");
validate.validators.length.tooShort = trans("frontendValidation.minimum %{count} chars");
validate.validators.length.tooLong = trans("frontendValidation.maximum %{count} chars");
validate.validators.url.message = trans("frontendValidation.is not a valid url");
validate.validators.email.message = trans("frontendValidation.is not a valid email");
validate.validators.format.message = trans("frontendValidation.is invalid");
validate.validators.numericality.notValid = trans("frontendValidation.is not a number");
validate.validators.numericality.notInteger = trans("frontendValidation.is not an integer");
validate.validators.numericality.notGreaterThan = trans("frontendValidation.must be greater than %{count}");
validate.validators.numericality.notGreaterThanOrEqualTo = trans("frontendValidation.must be greater than or equal to %{count}");
validate.validators.numericality.notEqualTo = trans("frontendValidation.must be equal to %{count}");
validate.validators.numericality.notLessThan = trans("frontendValidation.must be less than %{count}");
validate.validators.numericality.notLessThanOrEqualTo = trans("frontendValidation.must be less than or equal to %{count}");
validate.validators.numericality.notOdd = trans("frontendValidation.must be odd");
validate.validators.numericality.notEven = trans("frontendValidation.must be even");

/**
 * @param value
 * @param options
 * @param key
 * @param attributes
 * @returns {string|undefined}
 */
validate.validators.presenceAny = function(value, options, key, attributes) {
  const otherAttrs = options.keys.map(key => attributes[key]);

  if (!validate.isEmpty(value) || otherAttrs.some(val => !validate.isEmpty(val))) {
    return;
  }

  return trans("frontendValidation.At least one is required");
};

/**
 * @param value
 * @param options
 * @param key
 * @param attributes
 * @returns {string|undefined}
 */
validate.validators.acceptedAny = function(value, options, key, attributes) {
  const otherAttrs = options.keys.map(key => attributes[key]);

  if (value || otherAttrs.some(val => val)) {
    return;
  }

  return trans("frontendValidation.At least one must be checked");
};

/**
 * @param value
 * @param options
 * @param key
 * @param attributes
 * @returns {string|undefined}
 */
validate.validators.greaterThanZeroAny = function(value, options, key, attributes) {
  const otherAttrs = options.keys.map(key => attributes[key]);

  if (value || otherAttrs.some(val => val > 0)) {
    return;
  }

  return trans("frontendValidation.At least one must be greater than zero");
};

/**
 * @param value
 * @param options
 * @returns {string|undefined}
 */
validate.validators.checked = function(value, options) {
  if (value === true || value === "1" || value === 1 ) {
    return;
  }

  return options.message || trans("frontendValidation.Must be checked");
};

/**
 *
 * @param value
 * @param options
 * @returns {*|string}
 */
validate.validators.vatIdFormat = function (value, options) {
  // Empty VAT ID isn't invalid. Its presence should be checked outside this validator (e.g. with `presence` validator)
  if (value === null) {
    return;
  }

  const countryCodePattern = /^[A-Z]{2}/;
  if (!countryCodePattern.test(value.toUpperCase())) {
    return options.message || trans("frontendValidation.VAT ID must begin with a country code");
  }

  if (validateVatNumber(value)) {
    return;
  }

  return options.message || trans("frontendValidation.Invalid VAT ID");
};

/**
 * Validates the whole form, including any fieldsets and fields inside them.
 *
 * @param {HTMLFormElement} form
 * @param {Object} constraints
 * @param {Object} whitelist
 * @param {Object=} options
 *
 * @return {undefined|Object}
 */
export function validateForm(form, constraints, whitelist, options = {}) {
  const $form = $(form);
  const whitelistedConstraints = validate.cleanAttributes(constraints, whitelist);

  try {
    var localOptions = Object.assign({fullMessages: false}, options);
  } catch (e) {
    // IE11 workaround
    var localOptions = [];
    localOptions["fullMessages"] = false;
    for (let i in options) { localOptions[i] = options[i]; }
  }

  const errors = validate(form, whitelistedConstraints, localOptions);
  if (errors) {
    Object
      .keys(errors)
      .forEach(function(name) {
        const $el = $form.find(`[name="${name}"]`);

        rerenderValidation($el, errors[name]);
      });

    $form
      .find(".FormControlSet")
      .each((_, el) => rerenderSetValidation($(el), errors));
  }
  return errors;
}

/**
 * Validates single field (lefts other untouched whether valid or not).
 * Use on "change" or "input" events via `e.target`.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement} el
 * @param {Object} constraints
 * @param {Object} whitelist
 * @param {Object=} options
 *
 * @return {undefined|Object}
 */
export function validateField(form, el, constraints, whitelist, options = {}) {
  const $el = $(el);

  try {
    var singleConstraints = validate.cleanAttributes(constraints, Object.assign({[el.name]: true}, whitelist));
    var localOptions = Object.assign({fullMessages: false}, options);
  } catch (e) {
    // IE11 workaround
    var singleConstraints = [];
    singleConstraints[el.name] = true;
    for (let i in whitelist) { singleConstraints[i] = whitelist[i]; }

    var localOptions = [];
    localOptions["fullMessages"] = false;
    for (let i in options) { localOptions[i] = options[i]; }
  }

  const errors = validate(form, singleConstraints, localOptions);

  rerenderValidation($el, errors && errors[el.name]);

  return errors;
}

/**
 * Turns an attributes object to whitelist object (all values = `true`).
 *
 * @param {HTMLElement} el
 * @returns {Object}
 */
export function collectWhitelist(el) {
  const values = validate.collectFormValues(el);

  return Object
    .keys(values)
    .reduce(function(acc, name) {
      acc[name] = true;

      return acc;
    }, {});
}

/**
 * @param {jQuery} $el
 * @param {undefined|Object} errors
 */
export function rerenderValidation($el, errors) {
  const $box = $el.closest(".FormControlBox");

  $box
    .toggleClass("is-success", !errors)
    .toggleClass("is-error", !!errors)
    .find(".FormControlBox-error").remove();

  if (errors && errors.length > 0) {
    $box.append(renderBoxError(errors[0])); // Render only 1 error - the first one.

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "event": "form_error",
      "form_error_text": errors[0],
      "form_error_input": $el.attr("id") || $el.attr("name") || "Unknown field",
    });
  }
}

/**
 * @param {jQuery} $set
 * @param {undefined|Object} errors
 */
function rerenderSetValidation($set, errors) {
  if ($set.data("vd-no-validation") !== undefined) {
    return;
  }

  const myErrors = validate.cleanAttributes(errors, collectWhitelist($set));
  const hasErrors = Object.keys(myErrors).length > 0;

  $set
    .toggleClass("is-success", !hasErrors)
    .toggleClass("is-error", hasErrors)
    .find(".FormControlSet-message").remove();

  if (hasErrors) {
    $set.append(renderSetError());
  } else {
    $set.append(renderSetSuccess());
  }
}

/**
 * @param {string} msg
 *
 * @returns {string}
 */
function renderBoxError(msg) {
  return `
<span class="FormControlBox-error">${msg}</span>`;
}

/**
 * @returns {string}
 */
function renderSetError() {
  return `
<span class="FormControlSet-message FormControlSet-message--error">
  ${trans("frontendValidation.There are errors")}
</span>`;
}

/**
 * @returns {string}
 */
function renderSetSuccess() {
  return `
<span class="FormControlSet-message FormControlSet-message--success">
  ${trans("frontendValidation.All good")}
</span>`;
}
